import { useQueryClient } from "@tanstack/react-query";

import type { Agent, AgentVersion } from "src/agents/types";
import { agentKeys, BUILT_IN_TOOLS } from "src/api/agents/management";
import {
  useCopilotContextualEntities,
  useCopilotRefetchOnMutation,
} from "src/unifiedCopilot/context/hooks";
import type { ReferenceContextualEntity } from "src/unifiedCopilot/context/types";

type AgentTool = NonNullable<AgentVersion["configuration"]["tools"]>[number];

export const mapAgentToCopilotContextualEntity = (
  agent: Agent,
): ReferenceContextualEntity => ({
  kind: "reference",
  type: "agent",
  id: agent.id,
  displayName: agent.name,
});

export const mapAgentVersionToCopilotContextualEntity = (
  version: AgentVersion,
): ReferenceContextualEntity => ({
  kind: "reference",
  type: "agent-version",
  id: version.id,
  displayName: version.name,
  parent: {
    type: "agent",
    id: version.agent_id,
  },
});

export const mapAgentVersionsToCopilotContextualEntities = (
  versions: AgentVersion[],
): ReferenceContextualEntity[] =>
  versions.map(mapAgentVersionToCopilotContextualEntity);

const getToolTypeDisplayName = (
  type: AgentTool["type"],
): string | undefined => {
  if (!type) return undefined;

  const normalizedType = type.replaceAll("_", " ");
  return `${normalizedType.charAt(0).toUpperCase()}${normalizedType.slice(1)}`;
};

export const getAgentToolDisplayName = (tool: AgentTool): string => {
  const builtInName = BUILT_IN_TOOLS.find(
    (builtInTool) => builtInTool.type === tool.type,
  )?.name;
  if (builtInName) return builtInName;

  const configuredName = tool.name?.trim();
  if (configuredName) return configuredName;

  const typeDisplayName = getToolTypeDisplayName(tool.type);
  return typeDisplayName
    ? `Untitled tool (${typeDisplayName} tool)`
    : "Untitled tool";
};

export const mapAgentToolsToCopilotContextualEntities = (
  tools: AgentTool[],
  versionId: string,
): ReferenceContextualEntity[] =>
  tools.map((tool) => ({
    kind: "reference",
    type: "agent-tool",
    id: tool.id,
    displayName: getAgentToolDisplayName(tool),
    parent: {
      type: "agent-version",
      id: versionId,
    },
  }));

export const useAgentCopilotContext = (agent: Agent | undefined): void => {
  useCopilotContextualEntities(
    agent ? [mapAgentToCopilotContextualEntity(agent)] : [],
    { includeInAgentContext: true },
  );
};

export const useAgentVersionsCopilotContext = (
  versions: AgentVersion[] | undefined,
): void => {
  useCopilotContextualEntities(
    mapAgentVersionsToCopilotContextualEntities(versions ?? []),
  );
};

export const useAgentVersionCopilotContext = ({
  agent,
  currentVersion,
  versions,
}: {
  agent: Agent | undefined;
  currentVersion: AgentVersion | undefined;
  versions: AgentVersion[] | undefined;
}): void => {
  const automaticEntities: ReferenceContextualEntity[] = [
    ...(agent ? [mapAgentToCopilotContextualEntity(agent)] : []),
    ...(currentVersion
      ? [mapAgentVersionToCopilotContextualEntity(currentVersion)]
      : []),
  ];
  useCopilotContextualEntities(automaticEntities, {
    includeInAgentContext: true,
  });

  const siblingVersions = (versions ?? []).filter(
    (version) => version.id !== currentVersion?.id,
  );
  const mentionOnlyEntities: ReferenceContextualEntity[] = [
    ...mapAgentVersionsToCopilotContextualEntities(siblingVersions),
    ...mapAgentToolsToCopilotContextualEntities(
      currentVersion?.configuration.tools ?? [],
      currentVersion?.id ?? "",
    ),
  ];
  useCopilotContextualEntities(mentionOnlyEntities);
};

export const useAgentCopilotRefetch = ({
  workspaceId,
  agentId,
  versionId,
}: {
  workspaceId: string;
  agentId?: string;
  versionId?: string;
}): void => {
  const queryClient = useQueryClient();

  useCopilotRefetchOnMutation(async () => {
    const queryKeys: ReadonlyArray<readonly unknown[]> = [
      agentKeys.agents(workspaceId),
      ...(agentId
        ? [
            agentKeys.agent(workspaceId, agentId),
            agentKeys.versions(workspaceId, agentId),
          ]
        : []),
      ...(agentId && versionId
        ? [agentKeys.version(workspaceId, agentId, versionId)]
        : []),
    ];

    await Promise.all(
      queryKeys.map((queryKey) =>
        queryClient.invalidateQueries({ queryKey, exact: true }),
      ),
    );
  });
};
